import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Users, Mail, Loader2, Send, Trash2, Clock, CheckCircle2, Lock, Building2 } from "lucide-react";

/**
 * /team — Pro Unlimited only. Owner-facing team management.
 *
 * v2 (iter41.1): explicit workspace picker on invite + workspace badge on
 * every member/invitation + per-workspace seat counters.
 */
export default function Team() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [state, setState] = useState({
    members: [], invitations: [], seats_used: 0, seats_max: 3,
    workspaces: [], by_workspace: {}, active_workspace_id: null,
  });
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");
  const [error, setError] = useState("");
  const isProUnlimited = (user?.subscription?.tier || "").toLowerCase() === "pro_unlimited";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/members");
      setState(data);
      // Default the workspace picker to the caller's active workspace once
      // we know what workspaces exist.
      if (!workspaceId) {
        setWorkspaceId(data.active_workspace_id || data.workspaces?.[0]?.id || "");
      }
    } catch (e) {
      console.debug("[team] load failed:", e?.response?.status);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const selectedWorkspace = useMemo(
    () => state.workspaces.find(w => w.id === workspaceId),
    [state.workspaces, workspaceId]
  );
  const selectedSeats = state.by_workspace?.[workspaceId];
  const seatsUsedHere = selectedSeats?.seats_used || 0;
  const seatsMaxHere = selectedSeats?.seats_max || 3;
  const seatFull = seatsUsedHere >= seatsMaxHere;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!workspaceId) { setError("Pick a workspace first."); return; }
    setInviting(true);
    try {
      await api.post("/team/invite", { email: email.trim(), name: name.trim(), workspace_id: workspaceId });
      setEmail(""); setName("");
      toast.success(`Invitation sent · ${selectedWorkspace?.name || "workspace"}`);
      await load();
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const msg = typeof raw === "string" ? raw : (raw?.message || "Could not send invitation.");
      setError(msg);
    } finally {
      setInviting(false);
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this invitation?")) return;
    try {
      await api.delete(`/team/invitations/${id}`);
      toast.success("Invitation cancelled");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    }
  };

  const remove = async (userId, memberEmail) => {
    if (!window.confirm(`Remove ${memberEmail} from this workspace? They lose access immediately.`)) return;
    try {
      await api.delete(`/team/members/${userId}`);
      toast.success("Team member removed");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  if (!isProUnlimited) {
    return (
      <div data-testid="team-locked" className="max-w-2xl mx-auto p-6">
        <div className="dms-card p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-[#FEF3C7] flex items-center justify-center mb-4">
            <Lock className="w-6 h-6 text-[#B45309]" />
          </div>
          <h1 className="font-heading font-bold text-2xl text-[#0A2342]">Team executors are a Pro Unlimited feature</h1>
          <p className="mt-3 text-[#475569] text-[14.5px]">
            Invite up to 3 executors per workspace to receive the daily strategy prompt, run tools, mark tasks done and execute your calendar — while billing and settings stay with you.
          </p>
          <Link to="/billing" data-testid="team-upgrade" className="dms-btn-primary mt-6 inline-flex">Upgrade to Pro Unlimited</Link>
        </div>
      </div>
    );
  }

  const workspaces = state.workspaces || [];
  const perWs = state.by_workspace || {};

  return (
    <div data-testid="team-page" className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <div className="dms-label inline-flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Team</div>
        <h1 className="mt-2 font-heading font-bold text-2xl sm:text-3xl text-[#0A2342]">Your executors</h1>
        <p className="mt-2 text-[14.5px] text-[#475569] leading-relaxed">
          Invite up to 3 team members <b>per workspace</b>. Each executor is scoped to the workspace you assign — they only see that workspace, receive its daily post prompt and can run every DMS tool inside it. They never see billing or account settings.
        </p>
      </div>

      {/* Invite form */}
      <form onSubmit={submit} data-testid="team-invite-form" className="dms-card p-4 sm:p-5 mb-6">
        <div className="dms-label mb-3 inline-flex items-center gap-2"><Send className="w-3.5 h-3.5" /> Invite an executor</div>

        {workspaces.length > 1 && (
          <div className="mb-3">
            <label className="dms-label mb-1 block inline-flex items-center gap-1.5"><Building2 className="w-3 h-3" /> Assign to workspace</label>
            <select
              data-testid="team-invite-workspace"
              value={workspaceId}
              onChange={(e) => setWorkspaceId(e.target.value)}
              disabled={inviting}
              className="dms-input text-[14.5px] w-full sm:max-w-md"
            >
              {workspaces.map((w) => {
                const used = perWs[w.id]?.seats_used ?? 0;
                const max = perWs[w.id]?.seats_max ?? 3;
                return (
                  <option key={w.id} value={w.id} disabled={used >= max}>
                    {w.name} · {used}/{max} seats{used >= max ? " (full)" : ""}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            data-testid="team-invite-email"
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Their email" className="dms-input text-[14.5px]"
            disabled={inviting || seatFull}
          />
          <input
            data-testid="team-invite-name"
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Name (optional)" className="dms-input text-[14.5px]"
            disabled={inviting || seatFull}
          />
        </div>
        {error && <div data-testid="team-invite-error" className="mt-3 text-[13px] text-[#B91C1C]">{error}</div>}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-[12.5px] text-[#64748B]">
            {selectedWorkspace ? (
              <>{seatsUsedHere}/{seatsMaxHere} seats in <b>{selectedWorkspace.name}</b> · invitations expire in 7 days</>
            ) : (
              <>Pick a workspace to see seat counts</>
            )}
          </div>
          <button
            type="submit" data-testid="team-invite-submit"
            disabled={inviting || !email || seatFull || !workspaceId}
            className="dms-btn-primary"
          >
            {inviting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send invitation</>}
          </button>
        </div>
      </form>

      {/* Per-workspace member panels */}
      {loading ? (
        <div className="dms-card p-8 text-center text-[#64748B] flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : workspaces.length === 0 ? (
        <div className="dms-card p-8 text-center text-[#64748B] text-[14px]">No workspaces yet.</div>
      ) : (
        <div className="space-y-4">
          {workspaces.map((w) => {
            const panel = perWs[w.id] || { members: [], invitations: [], seats_used: 0, seats_max: 3 };
            const empty = panel.members.length === 0 && panel.invitations.length === 0;
            return (
              <div key={w.id} data-testid={`team-workspace-${w.id}`} className="dms-card p-4 sm:p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="dms-label inline-flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> {w.name}</div>
                  <div className="text-[12px] text-[#64748B]">{panel.seats_used}/{panel.seats_max} seats</div>
                </div>
                {empty ? (
                  <div data-testid={`team-empty-${w.id}`} className="py-6 text-center text-[#64748B] text-[13.5px]">
                    No team members in this workspace yet.
                  </div>
                ) : (
                  <ul className="divide-y divide-[#E2E8F0]">
                    {panel.members.map((m) => (
                      <li key={m.id} data-testid={`team-member-${m.user_id}`} className="py-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold text-[#0A2342] text-[14.5px] truncate">{m.name || m.email}</div>
                          <div className="text-[12.5px] text-[#64748B] truncate">{m.email} · joined {String(m.added_at || "").slice(0, 10)}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="inline-flex items-center gap-1 text-[11.5px] text-[#0F766E] bg-[#ECFDF5] border border-[#A7F3D0] rounded-full px-2.5 py-1"><CheckCircle2 className="w-3 h-3" /> Active</span>
                          <button
                            data-testid={`team-remove-${m.user_id}`}
                            onClick={() => remove(m.user_id, m.email)}
                            className="text-[#64748B] hover:text-[#B91C1C] p-1"
                            aria-label="Remove member"
                            title="Remove from workspace"
                          ><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </li>
                    ))}
                    {panel.invitations.map((i) => (
                      <li key={i.id} data-testid={`team-invitation-${i.id}`} className="py-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold text-[#0A2342] text-[14.5px] truncate">{i.email}</div>
                          <div className="text-[12.5px] text-[#64748B] truncate">Pending · expires {String(i.expires_at || "").slice(0, 10)}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="inline-flex items-center gap-1 text-[11.5px] text-[#B45309] bg-[#FEF3C7] border border-[#FCD34D] rounded-full px-2.5 py-1"><Clock className="w-3 h-3" /> Pending</span>
                          <button
                            data-testid={`team-cancel-${i.id}`}
                            onClick={() => cancel(i.id)}
                            className="text-[#64748B] hover:text-[#B91C1C] p-1"
                            aria-label="Cancel invitation"
                            title="Cancel invitation"
                          ><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
